import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { StructureComponent } from '../../../views/site/structure/structure.component';
import { AccessibilityHelper } from '../../accessibility.helper';
import { IButtonConfig } from '../../components/list-view/list-view-button-config.model';
import { BulkWorkflowDialog, BulkWorkflowDialogInitData } from '../../dialogs/bulk-workflow/bulk-workflow.dialog';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { DocumentPublishingPickerDialog } from '../../dialogs/document-publishing/document-publishing-picker.dialog';
import { ExportFormDataDialog, ExportFormDataDialogAction, ExportFormDataDialogConfig } from '../../dialogs/export-form-data/export-form-data.dialog';
import { ExportSiteDialog } from '../../dialogs/export-site/export-site.dialog';
import { ImportFormInstancesDialog } from '../../dialogs/import-form-data/import-form-instances.dialog';
import { ImportStructureInstancesDialog } from '../../dialogs/import-structure/import-structure.dialog';
import { SubscribeToWorkflowTransitionDialog } from '../../dialogs/subscribe/subscribe-to-workflow-transition/subscribe-to-workflow-transition.dialog';
import { FolderFormInstanceTreeService } from '../../services/folder-form-instance-tree.service';
import { Folder } from '../site-content/folder.model';
import { StructureButtonConfigClickHandlerBase } from './button-config-click-handler-base';
import { NewFolderContextMenuData, NewFormInstanceContextMenuData } from './page-menu-classes';

// Define handlers for the buttons displayed at the top of the structure component.
export class ShowHistoryClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        this.structureComponent.ShowHistory = true;
    }
}

export class SubscribeClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        let dialogRef = this.structureComponent.Dialog.open(SubscribeToWorkflowTransitionDialog, {
            width: '800px',
            data: {
                formInstance: this.structureComponent.CurrentFormInstance,
                folder: this.structureComponent.CurrentFolder
            }
        });
    }
}

export class PublishClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        let entityType: string, entityId: number;
        if (this.structureComponent.CurrentFormInstance != null) {
            entityType = 'forminstance';
            entityId = this.structureComponent.CurrentFormInstance.id;
        } else if (this.structureComponent.CurrentFolder != null) {
            entityType = 'folder';
            entityId = this.structureComponent.CurrentFolder.id;
        }

        this.structureComponent.DocumentPublishingService.getDocumentPublishingSettingsForSite(this.structureComponent.site.id, true).then(x => {
            let dialogRef = this.structureComponent.Dialog.open(DocumentPublishingPickerDialog, {
                width: '800px',
                data: {
                    settings: x,
                    entityType: entityType,
                    entityId: entityId
                }
            });
        });
    }
}

export class NewFolderClickHandler extends NewFolderContextMenuData {
    public constructor(structureComponent: StructureComponent) {
        super('na', structureComponent);
    }
}

export class NewFormClickHandler extends NewFormInstanceContextMenuData {
    public constructor(structureComponent: StructureComponent) {
        super('na', structureComponent);
    }
}

export class UpOneLevelClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        let idOfParent = null;
        if (this.structureComponent.CurrentFormInstance != null) {
            idOfParent = this.structureComponent.CurrentFolder.id;
        } else {
            idOfParent = this.structureComponent.CurrentFolder.parentFolderId;
        }
        if (idOfParent != null) {
            let parentFolder = new Folder();
            parentFolder.id = idOfParent;
            eventData.item = parentFolder;
            this.structureComponent.displayEntity(eventData);
        }
    }
}

export class ToggleShowHideClickHandler extends StructureButtonConfigClickHandlerBase {
    private folderTreeService: FolderFormInstanceTreeService;

    public constructor(structureComponent: StructureComponent, folderTreeService: FolderFormInstanceTreeService) {
        super(structureComponent);
        this.folderTreeService = folderTreeService;
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        // pharv 1/4/23 - to decouple structure and tree components, show/hide of the tree is now
        // done via the tree service
        this.folderTreeService.toggleShowTree();
        AccessibilityHelper.setFocus('#tree-drawer-keyboard-focus-div');
    }
}

export class ImportFormInstanceDataClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        // Process an import structure instances dialogue
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = this.structureComponent.CurrentFolder;

        const dialogRef = this.structureComponent.Dialog.open(ImportStructureInstancesDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(importedFormId => {
            // Note:  the import is performed in the dialog component class.
            if ((importedFormId != null) && (importedFormId > 0)) {
                this.structureComponent.reloadStructure();
            }
        });
    }
}

export class ImportFormInstanceFilesClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        // Process an import structure instances dialogue
        //
        // Note:  presently this dialogue only exports
        //        form instances.
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = this.structureComponent.CurrentFolder;

        const dialogRef = this.structureComponent.Dialog.open(ImportFormInstancesDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(importedFormId => {
            // Note:  the export is performed in the dialog component class.
            if ((importedFormId != null) && (importedFormId > 0)) {
                this.structureComponent.reloadStructure();
            }
        });
    }
}

export class ExportFormInstanceFilesClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        // Note:  this method current performs a full site export.
        //
        // Note:  we will likely allow the user to choose between
        //        a full site export or a more limited export in
        //        the future.
        let dialogConfig: MatDialogConfig = ExportSiteDialog.createDialogConfig(this.structureComponent.CurrentSiteService.Site);
        const dialogRef = this.structureComponent.Dialog.open(ExportSiteDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(exportedSiteDownloadUrl => {
            if (exportedSiteDownloadUrl != null) {
                // Note:  the following call to window.open() is not working.
                //window.open(exportedSiteDownloadUrl, "_blank");
            }
        });
    }
}

export class ExportToExcelClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        let currentFolder: Folder = this.structureComponent.CurrentFolderService.Folder;
        let dialogueData: ExportFormDataDialogConfig = new ExportFormDataDialogConfig(ExportFormDataDialogAction.ExportFolderFormData, currentFolder.id, currentFolder.dataCollectionId);

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = dialogueData; //currentFolder.id;
        const dialogRef = this.structureComponent.Dialog.open(ExportFormDataDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(downloadUrl => {
            this.structureComponent.reloadCurrentFolder(); //update folder after import
        });
    }
}

export class ImportFromExcelClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {

        let currentFolder: Folder = this.structureComponent.CurrentFolderService.Folder;

        let dialogueData: ExportFormDataDialogConfig = new ExportFormDataDialogConfig(ExportFormDataDialogAction.ExportFolderFormData, currentFolder.id, currentFolder.dataCollectionId);

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = dialogueData; //currentFolder.id;
        const dialogRef = this.structureComponent.Dialog.open(ExportFormDataDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(downloadUrl => {
            // Note:  nothing to do here by design.
        });
    }
}
export class SaveFormInstanceClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        if (this.structureComponent.FormFiller != null) {
            this.structureComponent.FormFiller.saveRenderer();
        }
    }
}

export class ResetFormInstanceClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent, private dialog: MatDialog) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(
                `Discard Edits`,
                `This action will revert all your edits back to their previously-saved values. Are you sure you want to discard your edits?`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                this.structureComponent.FormInstanceService.getContent(this.structureComponent.CurrentFormInstance.id, false, "Resetting Form...").then(res => {
                    this.structureComponent.CurrentFormInstance = res;

                    let hshEventData = { formInstance: this.structureComponent.CurrentFormInstance };
                    this.structureComponent.FormFiller.onFormInstanceReset(hshEventData);
                });
            }
        });
    }
}

export class PrintFormInstanceClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contextItem: any): void {
        //VNEXT-971 - KLW: Fixing print issue in new tab, have to pass "noreferrer" to prevent freezing of parent window
        window.open(`/site/${contextItem.dataCollectionId}/printable-form/${contextItem.id}?action=print`, '_blank', 'noreferrer');
    }
}

export class BulkWorkflowStateChangeClickHandler extends StructureButtonConfigClickHandlerBase {
    public constructor(structureComponent: StructureComponent) {
        super(structureComponent);
    }

    public handleButtonClick(buttonConfig: IButtonConfig, eventData: any, contectItem: any): void {
        let initData = new BulkWorkflowDialogInitData();
        let dialogRef = this.structureComponent.dialog.open(BulkWorkflowDialog, {
            height: 'auto',
            width: '400px;',
            /*
            data: {
                dataCollectionId: this.structureComponent.CurrentFolder.dataCollectionId,
                folderId: this.structureComponent.CurrentFolder.id
            }
            */
            data: initData
        });

        dialogRef.afterClosed().subscribe(x => {
            this.structureComponent.reloadStructure();
        });
    }
}
